import React from "react";

// Import types from the API file
type EligibilityCheck = {
  accountConnected: boolean;
  isProfessional: boolean;
  accountType: string | null;
  hasAdvertisePermission: boolean;
  businessConnected: boolean;
  pageConnected: boolean;
  instagramConnected: boolean;
  pageCount: number;
  adAccountEligible: boolean;
  hasAdAccount: boolean;
  adAccountStatus: number | null;
  adAccountActive: boolean;
  isEUEEA: boolean;
  euSubscriptionStatus: string | null;
};

type EligibilityRecommendation = {
  type: string;
  priority: string;
  message: string;
  details: string;
};

interface MetaAdsEligibilityModalProps {
  isOpen: boolean;
  eligible: boolean;
  reason: string | null;
  checks: EligibilityCheck;
  recommendations: EligibilityRecommendation[];
  onClose: () => void;
}

const MetaAdsEligibilityModal: React.FC<MetaAdsEligibilityModalProps> = ({
  isOpen,
  eligible,
  reason,
  checks,
  recommendations,
  onClose,
}) => {
  if (!isOpen) return null;

  // Determine if this is an EU subscription warning (allows continuation)
  const isEUWarning = reason === "EU_NO_SUBSCRIPTION";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md">
      <div className="relative w-full max-w-2xl mx-4 rounded-3xl bg-[#0B0F14] border border-white/10 shadow-2xl overflow-hidden">

        {/* Top gradient glow */}
        <div
          className="absolute top-0 left-0 right-0 h-32 opacity-40 blur-2xl"
          style={{
            background: eligible
              ? "linear-gradient(226.45deg, #1B76FF 12.8%, #2BFAA3 93.65%)"
              : "linear-gradient(226.45deg, #FF6B6B 12.8%, #FFE66D 93.65%)"
          }}
        />

        {/* Content */}
        <div className="relative px-10 py-10">
          {/* Header */}
          <div className="text-center mb-8">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  eligible ? "bg-green-400/20" : "bg-red-400/20"
                }`}
              >
                {eligible ? (
                  <svg
                    className="w-8 h-8 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-8 h-8 text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </div>
            </div>

            {/* Title */}
            <h2
              className={`text-3xl font-semibold mb-3 ${
                eligible ? "text-green-400" : "text-red-400"
              }`}
            >
              {eligible ? "Eligible for Meta Ads!" : "Not Eligible for Meta Ads"}
            </h2>

            {/* Description */}
            <p className="text-white/70 text-sm max-w-md mx-auto">
              {eligible
                ? isEUWarning
                  ? "Your account is eligible, but you're targeting EU/EEA countries. Make sure you have an active subscription."
                  : "Your account meets all the requirements to create Meta ad campaigns."
                : reason || "Your account does not meet the requirements to create Meta ad campaigns."}
            </p>
          </div>

          {/* Checks performed */}
          <div className="mb-8">
            <h3 className="text-white font-semibold text-sm mb-4">Account Verification Checks:</h3>
            <div className="space-y-3">
              {/* Account Connection Status */}
              <CheckItem
                label="Facebook Account Connected"
                passed={checks.accountConnected}
              />
              <CheckItem
                label="Professional Account Type"
                passed={checks.isProfessional}
              />
              <CheckItem
                label="Advertise Permission Granted"
                passed={checks.hasAdvertisePermission}
              />
              <CheckItem
                label="Business Account Connected"
                passed={checks.businessConnected}
              />
              <CheckItem
                label="Facebook Page Connected"
                passed={checks.pageConnected}
              />
              {checks.pageConnected && (
                <CheckItem
                  label="Instagram Account Connected"
                  passed={checks.instagramConnected}
                />
              )}
              <CheckItem
                label="Ad Account Available & Active"
                passed={checks.adAccountActive}
              />
              {checks.isEUEEA && (
                <CheckItem
                  label="EU/EEA Subscription Active"
                  passed={checks.euSubscriptionStatus === "active"}
                />
              )}
            </div>
          </div>

          {/* Recommendations if not eligible */}
          {!eligible && recommendations.length > 0 && (
            <div className="mb-8">
              <h3 className="text-white font-semibold text-sm mb-4">Recommended Actions:</h3>
              <div className="space-y-3">
                {recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-xl bg-white/5 border border-white/10"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                          rec.priority === "high"
                            ? "bg-red-400"
                            : rec.priority === "medium"
                            ? "bg-yellow-400"
                            : "bg-blue-400"
                        }`}
                      />
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium mb-1">
                          {rec.message}
                        </p>
                        <p className="text-white/60 text-xs">{rec.details}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Close button */}
          <div className="flex justify-center pt-4">
            <button
              onClick={onClose}
              className="px-8 py-3 rounded-full bg-white text-black font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              {eligible ? "Continue" : "Close"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper component for check items
interface CheckItemProps {
  label: string;
  passed: boolean;
}

const CheckItem: React.FC<CheckItemProps> = ({ label, passed }) => {
  return (
    <div className="flex items-center gap-3">
      <span
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
          passed
            ? "border-green-400 bg-green-400"
            : "border-red-400 bg-red-400"
        }`}
      >
        {passed ? (
          <span className="w-2 h-2 bg-black rounded-full" />
        ) : (
          <span className="text-black text-xs">✕</span>
        )}
      </span>
      <p
        className={`text-sm ${
          passed ? "text-white" : "text-white/60"
        }`}
      >
        {label}
      </p>
    </div>
  );
};

export default MetaAdsEligibilityModal;
